import {
  Box,
  Grow,
  Fade,
  Button,
  BoxProps,
  IconButton,
  Typography,
} from "@mui/material";
import { Icon } from "../Icon";
import { forwardRef, useState } from "react";
import { COLOR_CONSTANTS, TYPOGRAPHY_SCALE } from "../../constants/styles";
import useLocale from "src/domains/shared/hooks/localeHook/useLocale";
import { useTranslation } from "react-i18next";

export interface Map3DControlsModalProps extends BoxProps {}

export const Map3DControlsModal = forwardRef<
  HTMLDivElement,
  Map3DControlsModalProps
>(({ sx, ...props }, ref) => {
    const { t } = useTranslation("timeSeries");
  const [locale] = useLocale();
  const currentLocale = locale.locale as "ko" | "en";
  const [isOpen, setIsOpen] = useState(false);
  const onClose = () => setIsOpen((prev) => !prev);
  return (
    <Box ref={ref} sx={{ ...container, ...sx }} {...props}>
      {isOpen ? (
        <Grow in={isOpen} timeout={300} unmountOnExit>
          <Box sx={modal}>
            <Box sx={header}>
              <IconButton sx={iconButton} onClick={onClose}>
                <Icon name="20/etc/minus" size={16} sx={whiteIcon} clickable />
              </IconButton>
            </Box>
            <Box sx={iconBox}>
              <Icon
                name={
                  currentLocale === "ko"
                    ? "etc/control-rotate"
                    : "etc/control-rotate-en"
                }
                sx={controlIcon}
              />
              <Icon
                name={
                  currentLocale === "ko"
                    ? "etc/control-parallel"
                    : "etc/control-parallel-en"
                }
                sx={controlIcon}
              />
              <Icon
                name={
                  currentLocale === "ko"
                    ? "etc/control-zoom"
                    : "etc/control-zoom-en"
                }
                sx={controlIcon}
              />
            </Box>
          </Box>
        </Grow>
      ) : (
        <Fade in={!isOpen} timeout={300} unmountOnExit>
          <Button
            size="small"
            sx={button}
            startIcon={
              <Icon
                clickable
                size={16}
                sx={whiteIcon}
                name="etc/QuestionCircle"
              />
            }
            onClick={onClose}
          >
            <Typography sx={{ ...TYPOGRAPHY_SCALE.chipLabel, ...buttonText }}>
              {t("toolbar3d.howToUse", { ns: "timeSeries" })}
            </Typography>
          </Button>
        </Fade>
      )}
    </Box>
  );
});

Map3DControlsModal.displayName = "Map3DControlsModal";

const container = {
  opacity: 0.8,
} as const;

const modal = {
  width: "260px",
  height: "250px",
  padding: "10px 12px",
  borderRadius: "8px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  backgroundColor: "rgba(0, 0, 0, 0.56)",
} as const;

const button = {
  width: "87px",
  height: "30px",
  padding: "6px 8px",
  borderRadius: "8px",
  color: COLOR_CONSTANTS.primary.contrastText,
  backgroundColor: "rgba(0, 0, 0, 0.56)",
} as const;

const iconButton = {
  padding: 0,
} as const;

const iconBox = {
  padding: "16px 0",
  marginLeft: "10px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "12px",
  flex: 1,
  width: "100%",
} as const;

const header = {
  width: "100%",
  height: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  flexShrink: 0,
} as const;

const whiteIcon = {
  "& path": {
    fill: "#fff",
    fillOpacity: 1,
  },
} as const;

const controlIcon = {
  width: "90%",
  height: "100%",
} as const;

const buttonText = {
  marginLeft: "-5px",
  minWidth: "50px",
} as const;
